/**
 * @file AvitoFinanceOperationsTab.tsx
 * @description Таб «Операции» (phase14.0.4): таблица + фильтры (period / serviceType /
 * includeArchived) + пагинация.
 */

import { Archive, Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { Alert } from "../../../../components/ui/alert";
import { Button } from "../../../../components/ui/button";
import { EditorialCard } from "../../../../components/ui/EditorialCard";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { ApiError } from "../../../../lib/api-client";
import {
  formatPennyToRub,
  getServiceTypeStyle,
  shopAvitoFinance,
  type AvitoFinanceOperationDto,
} from "../../../../lib/api-shop-avito-finance";

interface Props {
  /** Если задан — заранее фильтруем по этому serviceType (например, CPA из тайла). */
  initialServiceType?: string;
}

const PAGE_SIZE = 50;

export function AvitoFinanceOperationsTab({ initialServiceType }: Props) {
  const [items, setItems] = useState<AvitoFinanceOperationDto[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [serviceType, setServiceType] = useState(initialServiceType ?? "");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await shopAvitoFinance.operations({
        from: from || undefined,
        to: to || undefined,
        serviceType: serviceType || undefined,
        includeArchived,
        page,
        pageSize: PAGE_SIZE,
      });
      setItems(result.items);
      setTotal(result.total);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось загрузить операции.");
    } finally {
      setLoading(false);
    }
  }, [from, to, serviceType, includeArchived, page]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (initialServiceType !== undefined) setServiceType(initialServiceType);
  }, [initialServiceType]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="flex flex-col gap-4">
      <EditorialCard>
        <EditorialCard.Body className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
          <div className="flex flex-col gap-1">
            <Label htmlFor="from">С</Label>
            <Input id="from" type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPage(1); }} />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="to">По</Label>
            <Input id="to" type="date" value={to} onChange={(e) => { setTo(e.target.value); setPage(1); }} />
          </div>
          <div className="flex flex-col gap-1">
            <Label htmlFor="st">Тип сервиса</Label>
            <Input
              id="st"
              placeholder="cpa / vas / vip / ..."
              value={serviceType}
              onChange={(e) => { setServiceType(e.target.value); setPage(1); }}
            />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => { setIncludeArchived(e.target.checked); setPage(1); }}
              className="size-4 rounded"
            />
            <span className="text-sm">Включая архивные</span>
          </label>
          <Button variant="outline" onClick={() => void load()}>Обновить</Button>
        </EditorialCard.Body>
      </EditorialCard>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <EditorialCard>
        <EditorialCard.Body className="p-0 overflow-x-auto">
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="size-5 animate-spin text-on-surface-variant" />
            </div>
          ) : items.length === 0 ? (
            <p className="p-6 text-sm text-on-surface-variant text-center">
              Операций нет. Нажмите «Получить с Avito» с отмеченной «История операций».
            </p>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="bg-surface-low text-xs text-on-surface-variant">
                <tr>
                  <th className="text-left px-3 py-2">Дата</th>
                  <th className="text-left px-3 py-2">Сервис</th>
                  <th className="text-left px-3 py-2">Операция</th>
                  <th className="text-right px-3 py-2">Сумма</th>
                  <th className="text-right px-3 py-2">Бонусы</th>
                  <th className="text-center px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((op) => {
                  const style = getServiceTypeStyle(op.serviceType);
                  return (
                    <tr key={op.id} className="border-t border-outline-variant">
                      <td className="px-3 py-2 whitespace-nowrap text-on-surface-variant">
                        {new Date(op.occurredAt).toLocaleString("ru-RU")}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-block px-2 py-0.5 rounded text-xs ${style.className}`}>
                          {op.serviceName}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-on-surface">{op.operationName}</td>
                      <td className="px-3 py-2 text-right font-medium">
                        {formatPennyToRub(op.amountRubPenny)}
                      </td>
                      <td className="px-3 py-2 text-right text-on-surface-variant">
                        {op.amountBonusPenny === "0" ? "—" : formatPennyToRub(op.amountBonusPenny)}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {op.archivedAt && (
                          <Archive
                            className="size-3.5 text-on-surface-variant inline"
                            aria-label="Архив"
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </EditorialCard.Body>
      </EditorialCard>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-on-surface-variant">
            Стр. {page} из {totalPages} (всего {total})
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>
              Назад
            </Button>
            <Button variant="outline" size="sm" disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>
              Вперёд
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
